import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtNumber, statusPill } from "../lib/utils";
import { Plus, Target, Pencil, RefreshCcw, Database, ExternalLink } from "lucide-react";

const STATUSES = ["planned", "in_progress", "completed"];

const EMPTY_FORM = {
  name: "", description: "", linked_kpis: [], status: "planned",
  expected_impact: "", capex_usd: 0, opex_savings_usd: 0, roi_years: null,
  owner: "", target_completion: "", data_sources: [],
};

export default function Initiatives() {
  const [rows, setRows] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // initiative being edited
  const [detail, setDetail] = useState(null);   // initiative dependency view
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/initiatives"), api.get("/catalogue/kpis"),
    ]);
    setRows(a.data);
    setCatalogue(b.data.kpis);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setErr("");
    setShowCreate(true);
  };

  const openEdit = (init) => {
    setForm({
      name: init.name || "",
      description: init.description || "",
      linked_kpis: init.linked_kpis || [],
      status: init.status || "planned",
      expected_impact: init.expected_impact || "",
      capex_usd: init.capex_usd || 0,
      opex_savings_usd: init.opex_savings_usd || 0,
      roi_years: init.roi_years || null,
      owner: init.owner || "",
      target_completion: init.target_completion || "",
      data_sources: init.data_sources || [],
    });
    setErr("");
    setEditing(init);
  };

  const submitCreate = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/initiatives", {
        ...form,
        capex_usd: Number(form.capex_usd),
        opex_savings_usd: Number(form.opex_savings_usd),
      });
      setShowCreate(false);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to create"); }
  };

  const submitEdit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.put(`/initiatives/${editing.id}`, {
        ...form,
        capex_usd: Number(form.capex_usd),
        opex_savings_usd: Number(form.opex_savings_usd),
      });
      setEditing(null);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to save"); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/initiatives/${id}/status`, null, { params: { status } });
    await load();
  };

  const refreshInitiative = async (id) => {
    setBusy(id);
    try {
      await api.post(`/initiatives/${id}/refresh`);
      await load();
    } finally { setBusy(""); }
  };

  const grouped = {
    planned: rows.filter(r => r.status === "planned"),
    in_progress: rows.filter(r => r.status === "in_progress"),
    completed: rows.filter(r => r.status === "completed"),
  };

  const kpiNameById = (id) => catalogue.find((k) => k.id === id)?.name || id;

  return (
    <div className="p-6 space-y-4" data-testid="initiatives-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="section-label">Improvement</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Initiatives</h1>
          <p className="text-sm text-ink-soft">Track ESG improvement programs, their dependent KPIs, data sources, and last refresh.</p>
        </div>
        <button data-testid="open-create-initiative" onClick={openCreate} className="btn btn-primary"><Plus size={14}/> New Initiative</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {STATUSES.map(s => (
          <div key={s} className="card p-3" data-testid={`column-${s}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={statusPill(s)}>{s.replaceAll("_", " ")}</span>
              <span className="text-xs text-ink-soft">{grouped[s].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[s].map(i => (
                <div key={i.id} className="card-soft p-3" data-testid={`init-${i.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Target size={12} className="text-brand shrink-0"/>
                      <div className="font-medium text-sm truncate">{i.name}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        data-testid={`init-detail-${i.id}`}
                        onClick={() => setDetail(i)}
                        className="text-ink-soft hover:text-brand"
                        title="View dependencies & data sources"
                      ><Database size={13}/></button>
                      <button
                        data-testid={`init-edit-${i.id}`}
                        onClick={() => openEdit(i)}
                        className="text-ink-soft hover:text-brand"
                        title="Edit initiative"
                      ><Pencil size={13}/></button>
                      <button
                        data-testid={`init-refresh-${i.id}`}
                        onClick={() => refreshInitiative(i.id)}
                        disabled={busy === i.id}
                        className="text-ink-soft hover:text-brand"
                        title="Mark as freshly reviewed against current data sources"
                      >
                        <RefreshCcw size={13} className={busy === i.id ? "animate-spin" : ""}/>
                      </button>
                    </div>
                  </div>
                  <div className="text-[12px] text-ink-soft mb-2 line-clamp-2">{i.description}</div>
                  <div className="text-[11px] text-ink-soft mb-1"><span className="font-semibold text-ink">Impact:</span> {i.expected_impact || "—"}</div>
                  <div className="grid grid-cols-3 gap-2 text-[11px] mt-2">
                    <div><div className="text-ink-soft">CapEx</div><div className="font-mono">${fmtNumber(i.capex_usd, 0)}</div></div>
                    <div><div className="text-ink-soft">OpEx Δ</div><div className="font-mono">{i.opex_savings_usd >= 0 ? "-" : "+"}${fmtNumber(Math.abs(i.opex_savings_usd), 0)}</div></div>
                    <div><div className="text-ink-soft">ROI</div><div className="font-mono">{i.roi_years ? `${i.roi_years}y` : "—"}</div></div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(i.linked_kpis || []).map(k => (
                      <Link key={k} to={`/kpi/${k}`} className="pill text-[10px] hover:bg-brand-100" title={kpiNameById(k)}>
                        {kpiNameById(k).length > 22 ? kpiNameById(k).slice(0,22) + "…" : kpiNameById(k)}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-ink-soft">
                    <span>{i.owner || "—"}</span>
                    <select
                      data-testid={`status-select-${i.id}`}
                      value={i.status}
                      onChange={e=>updateStatus(i.id, e.target.value)}
                      className="border border-border rounded px-1 py-0.5 text-[11px]"
                    >
                      {STATUSES.map(x => <option key={x} value={x}>{x.replaceAll("_"," ")}</option>)}
                    </select>
                  </div>
                  <div className="mt-2 pt-2 border-t border-border text-[10px] text-ink-soft flex items-center justify-between">
                    <span title={i.modified_at}>Modified {fmtRelative(i.modified_at)}</span>
                    <span title={i.last_refreshed_at}>Refreshed {fmtRelative(i.last_refreshed_at)}</span>
                  </div>
                </div>
              ))}
              {grouped[s].length === 0 && <div className="text-xs text-ink-soft py-4 text-center">No initiatives</div>}
            </div>
          </div>
        ))}
      </div>

      {/* CREATE modal */}
      {showCreate && (
        <FormModal
          title="Create Initiative"
          form={form} setForm={setForm}
          catalogue={catalogue}
          onClose={() => setShowCreate(false)}
          onSubmit={submitCreate}
          err={err}
          submitLabel="Create"
          testidPrefix="init-create"
        />
      )}
      {/* EDIT modal */}
      {editing && (
        <FormModal
          title={`Edit · ${editing.name}`}
          form={form} setForm={setForm}
          catalogue={catalogue}
          onClose={() => setEditing(null)}
          onSubmit={submitEdit}
          err={err}
          submitLabel="Save changes"
          testidPrefix="init-edit"
        />
      )}

      {/* Dependency / data-source detail panel */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="init-detail-modal">
          <div className="card w-full max-w-2xl p-5 bg-white max-h-[85vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="section-label">Initiative dependencies</div>
                <div className="font-semibold mt-1">{detail.name}</div>
                <div className="text-xs text-ink-soft">{detail.description}</div>
              </div>
              <button data-testid="init-detail-close" onClick={() => setDetail(null)} className="btn btn-ghost">Close</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <div className="dlabel">Linked KPIs (dependencies)</div>
                <ul className="mt-2 space-y-1" data-testid="init-detail-kpis">
                  {(detail.linked_kpis || []).map((k) => (
                    <li key={k} className="text-sm">
                      <Link to={`/kpi/${k}`} className="text-brand hover:underline inline-flex items-center gap-1">
                        <ExternalLink size={11}/> {kpiNameById(k)}
                      </Link>
                    </li>
                  ))}
                  {(detail.linked_kpis || []).length === 0 && <li className="text-xs text-ink-soft">No KPI dependencies</li>}
                </ul>
              </div>
              <div>
                <div className="dlabel">Data sources</div>
                <ul className="mt-2 space-y-1" data-testid="init-detail-sources">
                  {(detail.data_sources || []).map((s) => (
                    <li key={s} className="text-sm flex items-center gap-2">
                      <Database size={11} className="text-ink-soft"/> {s}
                    </li>
                  ))}
                  {(detail.data_sources || []).length === 0 && <li className="text-xs text-ink-soft">No data sources configured</li>}
                </ul>
              </div>
            </div>
            <div className="mt-5 pt-3 border-t border-border grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-ink-soft">Created</div>
                <div className="font-mono">{fmtDate(detail.created_at)}</div>
              </div>
              <div>
                <div className="text-ink-soft">Last modified</div>
                <div className="font-mono">{fmtDate(detail.modified_at)} <span className="text-ink-soft">· by {detail.modified_by || "—"}</span></div>
              </div>
              <div>
                <div className="text-ink-soft">Last refreshed</div>
                <div className="font-mono">{fmtDate(detail.last_refreshed_at)}</div>
              </div>
              <div>
                <div className="text-ink-soft">Owner</div>
                <div>{detail.owner || "—"}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button data-testid="init-detail-refresh" onClick={() => refreshInitiative(detail.id)} className="btn btn-secondary"><RefreshCcw size={12}/> Refresh now</button>
              <button data-testid="init-detail-edit" onClick={() => { setDetail(null); openEdit(detail); }} className="btn btn-primary"><Pencil size={12}/> Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const day = 86400000;
  if (diff < day) return "today";
  if (diff < 2 * day) return "yesterday";
  if (diff < 30 * day) return `${Math.round(diff / day)}d ago`;
  if (diff < 365 * day) return `${Math.round(diff / (30 * day))}mo ago`;
  return `${Math.round(diff / (365 * day))}y ago`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().slice(0, 19).replace("T", " "); } catch { return iso; }
}

function FormModal({ title, form, setForm, catalogue, onClose, onSubmit, err, submitLabel, testidPrefix }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={`${testidPrefix}-modal`}>
      <div className="card w-full max-w-lg p-5 bg-white max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="section-label">{title}</div>
        <form onSubmit={onSubmit} className="mt-3 space-y-3 text-sm">
          <div>
            <label className="dlabel">Name</label>
            <input data-testid={`${testidPrefix}-name`} required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm outline-none focus:border-brand"/>
          </div>
          <div>
            <label className="dlabel">Description</label>
            <textarea data-testid={`${testidPrefix}-desc`} required value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm outline-none focus:border-brand h-20"/>
          </div>
          <div>
            <label className="dlabel">Expected impact</label>
            <input data-testid={`${testidPrefix}-impact`} value={form.expected_impact} onChange={e=>setForm({...form, expected_impact:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm outline-none focus:border-brand"/>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="dlabel">CapEx (USD)</label>
              <input data-testid={`${testidPrefix}-capex`} type="number" value={form.capex_usd} onChange={e=>setForm({...form, capex_usd:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
            </div>
            <div>
              <label className="dlabel">OpEx savings</label>
              <input data-testid={`${testidPrefix}-opex`} type="number" value={form.opex_savings_usd} onChange={e=>setForm({...form, opex_savings_usd:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
            </div>
            <div>
              <label className="dlabel">Owner</label>
              <input data-testid={`${testidPrefix}-owner`} value={form.owner} onChange={e=>setForm({...form, owner:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="dlabel">Status</label>
              <select data-testid={`${testidPrefix}-status`} value={form.status} onChange={e=>setForm({...form, status:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm">
                {["planned","in_progress","completed"].map(s => <option key={s} value={s}>{s.replaceAll("_"," ")}</option>)}
              </select>
            </div>
            <div>
              <label className="dlabel">Target completion</label>
              <input data-testid={`${testidPrefix}-target`} type="date" value={form.target_completion || ""} onChange={e=>setForm({...form, target_completion:e.target.value})} className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"/>
            </div>
          </div>
          <div>
            <label className="dlabel">Linked KPIs (dependencies)</label>
            <select multiple data-testid={`${testidPrefix}-kpis`}
              value={form.linked_kpis}
              onChange={e=>setForm({...form, linked_kpis: Array.from(e.target.selectedOptions).map(o=>o.value)})}
              className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm h-28">
              {catalogue.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
            <div className="text-[10px] text-ink-soft mt-1">Hold ⌘/Ctrl to select multiple</div>
          </div>
          <div>
            <label className="dlabel">Data sources (one per line)</label>
            <textarea
              data-testid={`${testidPrefix}-sources`}
              value={(form.data_sources || []).join("\n")}
              onChange={e=>setForm({...form, data_sources: e.target.value.split("\n").map(s=>s.trim()).filter(Boolean)})}
              placeholder={"e.g.\nManila utility bills\nGRC incident register"}
              className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm outline-none focus:border-brand h-20"
            />
          </div>
          {err && <div className="text-status-error text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button data-testid={`${testidPrefix}-submit`} type="submit" className="btn btn-primary">{submitLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
